// eslint-disable-next-line no-unused-vars
import { useParams, useLocation } from "react-router-dom";
import React, { useContext, useEffect, useState } from "react";
import { FirebaseContext } from "../../firebaseComponents";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
// components
import Spinner from "../../components/Spinner";
// util
import { showErrors, updateDateFormat } from "../../utils/helpers";
// styles
import styles from "./forms.module.css";

const requiredString = "Required!";

export default function DestructionFormPage() {
  const firebase = useContext(FirebaseContext);
  let { id } = useParams();
  let location = useLocation();
  let thanks = location.search.includes("thanks");
  const submitMessage =
    "Form submitted!\n Your request has been received. Once a decision has been made about your request, you will be notified via email. Thank you.";
  const [message, setMessage] = useState(thanks ? submitMessage : null);
  const [errorMessage, setErrorMessage] = useState(null);

  useEffect(() => {
    firebase.db
      .ref(`certificate_of_destruction_form/${id}/formId`)
      .once("value")
      .then((snapshot) => {
        if (snapshot.exists()) {
          setMessage(
            "Your certificate of destruction form has been submitted, for any questions please contact us."
          );
        }
      });
  }, []);

  const Schema = Yup.object().shape({
    vialsDestroyed: Yup.number().required(requiredString),
    hospitalName: Yup.string().required(requiredString),
    hospitalAddress: Yup.string().required(requiredString),
    authorizedRepresentativeName: Yup.string().required(requiredString),
    authorizedRepresentativeTitle: Yup.string().required(requiredString),
    email: Yup.string().required(requiredString),
    phone: Yup.string().required(requiredString),
    eSignature: Yup.string().required(requiredString),
    date: Yup.string().required(requiredString),
  });

  return (
    <div className={styles.wrapper}>
      <h2 className={styles.title}>
        GIAPREZA (angiotensin II) Certificate of Destruction
      </h2>

      {message ? (
        <div className={styles.message}>
          {message.split("\n").map((val, i) => (
            <div key={i}>{val}</div>
          ))}
        </div>
      ) : (
        <>
          <div className={styles.formDesc}>
            This form certifies the destruction of spoiled, or otherwise unable
            to be administered, GIAPREZA vials in connection with a request for
            GIAPREZA replacement vials under the GIAPREZA (angiotensin II)
            Wastage Product Replacement Program, with destruction performed per
            the policy of the hospital listed below. Following the destruction
            of GIAPREZA vials and submission of this GIAPREZA (angiotensin II)
            Certificate of Destruction Form, La Jolla shall issue vials of the
            same NDC of GIAPREZA that were originally purchased by the hospital.
            The GIAPREZA replacement vials shall be shipped to the hospital by
            La Jolla's third-party logistics provider at no cost to the
            hospital.{" "}
          </div>
          <Formik
            initialValues={{
              vialsDestroyed: "",
              hospitalName: "",
              hospitalAddress: "",
              authorizedRepresentativeName: "",
              authorizedRepresentativeTitle: "",
              email: "",
              phone: "",
              eSignature: "",
              date: "",
            }}
            validationSchema={Schema}
            validateOnBlur={false}
            validateOnChange={false}
            onSubmit={(values) => {
              setErrorMessage(null);
              let valuesCopy = { ...values, formId: id };
              valuesCopy.date = updateDateFormat(valuesCopy.date);

              return firebase.db
                .ref("certificate_of_destruction_form/" + id)
                .set(valuesCopy, (error) => {
                  if (error) {
                    setErrorMessage("Something went wrong. Please try again.");
                  } else {
                    setMessage(submitMessage);
                    window.scrollTo(0, 0);
                  }
                });
            }}
          >
            {({ errors, touched, isSubmitting }) => (
              <Form>
                <div className={styles.formSection}>
                  <div
                    className={`${styles.half} ${
                      errors.vialsDestroyed && touched.vialsDestroyed
                        ? styles.inputError
                        : ""
                    }`}
                  >
                    <label className={styles.label}>
                      Number of GIAPREZA Vials Destroyed:
                    </label>
                    <Field
                      name="vialsDestroyed"
                      type="number"
                      className={styles.input}
                    />
                  </div>
                </div>

                <div className={styles.formSection}>
                  <div
                    className={`${styles.full} ${
                      errors.hospitalName && touched.hospitalName
                        ? styles.inputError
                        : ""
                    }`}
                  >
                    <label className={styles.label}>Hospital Name:</label>
                    <Field name="hospitalName" className={styles.input} />
                  </div>
                </div>

                <div className={styles.formSection}>
                  <div
                    className={`${styles.full} ${
                      errors.hospitalAddress && touched.hospitalAddress
                        ? styles.inputError
                        : ""
                    }`}
                  >
                    <label className={styles.label}>Hospital Address:</label>
                    <Field name="hospitalAddress" className={styles.input} />
                  </div>
                </div>

                <div className={styles.formSection}>
                  <div
                    className={`${styles.half} ${
                      errors.authorizedRepresentativeName &&
                      touched.authorizedRepresentativeName
                        ? styles.inputError
                        : ""
                    }`}
                  >
                    <label className={styles.label}>
                      Authorized Representative Name:
                    </label>
                    <Field
                      name="authorizedRepresentativeName"
                      className={styles.input}
                    />
                  </div>

                  <div
                    className={`${styles.half} ${
                      errors.authorizedRepresentativeTitle &&
                      touched.authorizedRepresentativeTitle
                        ? styles.inputError
                        : ""
                    }`}
                  >
                    <label className={styles.label}>
                      Authorized Representative Title:
                    </label>
                    <Field
                      name="authorizedRepresentativeTitle"
                      className={styles.input}
                    />
                  </div>
                </div>

                <div className={styles.formSection}>
                  <div
                    className={`${styles.half} ${
                      errors.email && touched.email ? styles.inputError : ""
                    }`}
                  >
                    <label className={styles.label}>Email Address:</label>
                    <Field name="email" className={styles.input} type="email" />
                  </div>

                  <div
                    className={`${styles.half} ${
                      errors.phone && touched.phone ? styles.inputError : ""
                    }`}
                  >
                    <label className={styles.label}>Phone Number:</label>
                    <Field name="phone" className={styles.input} />
                  </div>
                </div>

                <div className={styles.formSection}>
                  <div
                    className={`${styles.half} ${
                      errors.eSignature && touched.eSignature
                        ? styles.inputError
                        : ""
                    }`}
                  >
                    <label className={styles.label}>E-Signature:</label>
                    <Field name="eSignature" className={styles.input} />
                  </div>

                  <div
                    className={`${styles.half} ${
                      errors.date && touched.date ? styles.inputError : ""
                    }`}
                  >
                    <label className={styles.label}>Date:</label>
                    <Field name="date" className={styles.input} type="date" />
                  </div>
                </div>

                <div className={styles.submitContainer}>
                  {errorMessage ? (
                    <div className={styles.errorMessage}>{errorMessage}</div>
                  ) : showErrors(errors, touched) ? (
                    <div className={styles.errorMessage}>
                      Error: Please complete all fields
                    </div>
                  ) : null}
                  <button
                    type="submit"
                    className={styles.submitButton}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? <Spinner /> : null}
                    <span
                      className={
                        isSubmitting ? styles.submitButtonSpacing : null
                      }
                    >
                      Submit
                    </span>
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </>
      )}
    </div>
  );
}
